/**
 * Map cricket innings (batting order) onto fixture home/away team rows.
 *
 * Scorecards store inns1..inns4 in batting order (toss decides who bats first).
 * UI rows stay in fixture order (home then away). When bat_now is present we remap;
 * when it is missing we fall back to inns1→home / inns2→away (cannot know toss order).
 */

export type CricketInnsScoreStrings = [string, string, string, string];

export type CricketScorecardLike = {
  inns1?: unknown;
  inns2?: unknown;
  inns3?: unknown;
  inns4?: unknown;
  inns_now?: unknown;
  bat_now?: unknown;
  batNow?: unknown;
  [key: string]: unknown;
};

export function formatCricketInnsScore(inns: unknown): string {
  if (!inns || typeof inns !== "object") return "0/0";
  const row = inns as { runs?: number | string | null; wkts?: number | string | null };
  const r = row.runs != null ? Number(row.runs) : 0;
  const w = row.wkts != null ? Number(row.wkts) : 0;
  return `${Number.isFinite(r) ? r : 0}/${Number.isFinite(w) ? w : 0}`;
}

/** Normalize scorecard from object or array-wrapped stream message. */
export function unwrapCricketScorecard(raw: unknown): CricketScorecardLike | null {
  if (raw == null) return null;
  const sc = Array.isArray(raw)
    ? ((raw[0] as { msg_data?: unknown } | undefined)?.msg_data ?? raw[0])
    : raw;
  if (!sc || typeof sc !== "object" || Array.isArray(sc)) return null;
  return sc as CricketScorecardLike;
}

export function extractCricketInnsScores(scorecard: CricketScorecardLike): CricketInnsScoreStrings {
  return [
    scorecard.inns1 != null ? formatCricketInnsScore(scorecard.inns1) : "0/0",
    scorecard.inns2 != null ? formatCricketInnsScore(scorecard.inns2) : "0/0",
    scorecard.inns3 != null ? formatCricketInnsScore(scorecard.inns3) : "0/0",
    scorecard.inns4 != null ? formatCricketInnsScore(scorecard.inns4) : "0/0",
  ];
}

function normalizeTeamLabelKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/** Initials / short code for multi-word teams (`New Zealand` → `NZ`); 3-letter slice for single words. */
function cricketTeamAbbreviation(teamName: string): string {
  const words = String(teamName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) {
    return words[0]!.replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase();
  }
  return words
    .map((w) => w.replace(/[^A-Za-z0-9]/g, ""))
    .filter(Boolean)
    .map((w) => w[0]!)
    .join("")
    .slice(0, 3)
    .toUpperCase();
}

/** Scorecard `bat_now` vs fixture labels often differ (“NZ” vs “New Zealand”). */
export function cricketTeamLabelsMatch(a: string | undefined | null, b: string | undefined | null): boolean {
  const left = String(a ?? "").trim();
  const right = String(b ?? "").trim();
  if (!left || !right) return false;
  if (left === right) return true;
  const na = normalizeTeamLabelKey(left);
  const nb = normalizeTeamLabelKey(right);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.includes(nb) || nb.includes(na)) return true;
  const leftUpper = left.toUpperCase();
  const rightUpper = right.toUpperCase();
  const leftAbbr = cricketTeamAbbreviation(left);
  const rightAbbr = cricketTeamAbbreviation(right);
  if (leftAbbr.length >= 2 && leftAbbr === rightUpper) return true;
  if (rightAbbr.length >= 2 && rightAbbr === leftUpper) return true;
  return false;
}

/**
 * True when the batting label maps to the fixture home side.
 * Ambiguous / unknown labels return null so callers can skip remapping.
 */
export function batNowIsHomeTeam(
  batNow: string,
  teams: readonly string[],
): boolean | null {
  const home = String(teams[0] ?? "").trim();
  const away = String(teams[1] ?? "").trim();
  const matchHome = cricketTeamLabelsMatch(batNow, home);
  const matchAway = cricketTeamLabelsMatch(batNow, away);
  if (matchHome && !matchAway) return true;
  if (matchAway && !matchHome) return false;
  if (matchHome && matchAway) {
    const batKey = normalizeTeamLabelKey(batNow);
    const homeKey = normalizeTeamLabelKey(home);
    const awayKey = normalizeTeamLabelKey(away);
    if (batKey === homeKey && batKey !== awayKey) return true;
    if (batKey === awayKey && batKey !== homeKey) return false;
    // Prefer the closer-length label (“India” vs “India A”).
    const homeDist = Math.abs(homeKey.length - batKey.length);
    const awayDist = Math.abs(awayKey.length - batKey.length);
    if (homeDist !== awayDist) return homeDist < awayDist;
    return batNow.trim() === home;
  }
  return null;
}

/**
 * Assign inns1..inns4 onto [homePrimary, awayPrimary, homeSecond, awaySecond]
 * using bat_now + inns_now when available.
 *
 * `fallbackBatNow` — last known batting side when the live frame omitted it (orientation latch).
 */
export function mapCricketInnsScoresToTeams(
  scorecard: CricketScorecardLike,
  teams: readonly string[],
  fallbackBatNow?: string | null,
): { scores: CricketInnsScoreStrings; batNow: string } {
  const insScore = extractCricketInnsScores(scorecard);
  const liveBatNow = String(scorecard.bat_now ?? scorecard.batNow ?? "").trim();
  const batNow = liveBatNow || String(fallbackBatNow ?? "").trim();

  if (!batNow) {
    return { scores: insScore, batNow: "" };
  }

  const batIsHome = batNowIsHomeTeam(batNow, teams);
  // Unknown batting label: keep batting-order scores rather than guessing away.
  if (batIsHome == null) {
    return { scores: insScore, batNow: liveBatNow || batNow };
  }

  const innsNow = Number(scorecard.inns_now) || 1;
  const oppNow = innsNow === 1 || innsNow === 3 ? 2 : 1;
  const rptNow = innsNow === 1 || innsNow === 3 ? 1 : 2;
  const idx0 = batIsHome ? rptNow - 1 : oppNow - 1;
  const idx1 = batIsHome ? oppNow - 1 : rptNow - 1;
  const idx2 = batIsHome ? rptNow + 1 : oppNow + 1;
  const idx3 = batIsHome ? oppNow + 1 : rptNow + 1;

  return {
    scores: [
      insScore[idx0] || "0/0",
      insScore[idx1] || "0/0",
      insScore[idx2] || "0/0",
      insScore[idx3] || "0/0",
    ],
    batNow: liveBatNow || batNow,
  };
}
